import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DashboardWorkbench } from "@/components/dashboard/DashboardWorkbench";
import { MetricCards } from "@/components/dashboard/MetricCards";
import { RecentComplaintsCard } from "@/components/dashboard/RecentComplaintsCard";
import { listComplaints, startIntake } from "@/lib/api";

const statusClassMap = {
  Created: "sp-pend",
  Acknowledged: "sp-ack",
  InReview: "sp-pend",
  Resolved: "sp-res",
  Archived: "sp-res",
};

export const ApplicantWorkspacePage = ({ session, language }) => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState("dashboard");
  const [complaints, setComplaints] = useState([]);
  const [category, setCategory] = useState("Land Dispute");
  const [boothId, setBoothId] = useState("");

  const load = async () => {
    try {
      const list = await listComplaints(session.token);
      setComplaints(list);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to load applicant records");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startComplaint = async () => {
    try {
      const intake = await startIntake(session.token, {
        language,
        category,
        booth_id: boothId || null,
        channel: boothId ? "booth" : "online",
      });
      sessionStorage.setItem("aavedak-intake-session-id", intake.session_id);
      sessionStorage.setItem("aavedak-intake-category", category);
      sessionStorage.setItem("aavedak-intake-channel", boothId ? "booth" : "online");
      sessionStorage.setItem("aavedak-intake-greeting", intake.opening_question);
      navigate("/workspace/intake");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to start complaint intake");
    }
  };

  const metricCards = [
    {
      label: "My Complaints",
      value: complaints.length,
      badge: "Applicant view",
      badgeClass: "badge-gray",
    },
    {
      label: "In Progress",
      value: complaints.filter((item) => ["Created", "Acknowledged", "InReview"].includes(item.status)).length,
      badge: "Open",
      badgeClass: "badge-amber",
    },
    {
      label: "Resolved",
      value: complaints.filter((item) => item.status === "Resolved").length,
      badge: "Closed",
      badgeClass: "badge-green",
    },
    {
      label: "Urgent",
      value: complaints.filter((item) => item.urgency_flag).length,
      badge: "Priority",
      badgeClass: "badge-blue",
    },
  ];

  const rows = complaints.map((item) => ({
    ...item,
    statusClass: statusClassMap[item.status] || "sp-pend",
    dateLabel: item.created_at ? new Date(item.created_at).toLocaleDateString("en-IN", { month: "short", day: "2-digit" }) : "-",
  }));

  return (
    <DashboardWorkbench
      session={session}
      workspaceTitle="Applicant Dashboard"
      navSections={[
        {
          title: "My Workspace",
          items: [
            { key: "dashboard", label: "Dashboard", icon: "📊" },
            { key: "newcase", label: "File New Complaint", icon: "📝" },
            { key: "history", label: "Complaint History", icon: "📁" },
          ],
        },
      ]}
      activeKey={activeNav}
      onNavigate={setActiveNav}
      topActions={
        <>
          <Button variant="outline" className="btn-sm" onClick={load} data-testid="applicant-refresh-button">Refresh</Button>
          <Button className="btn-sm btn-sm-orange" onClick={startComplaint} data-testid="applicant-new-complaint-button">+ New Complaint</Button>
        </>
      }
    >
      {activeNav === "dashboard" ? (
        <>
          <MetricCards cards={metricCards} />
          <div className="card" data-testid="applicant-recent-card">
            <div className="card-head">
              <h3>My Recent Complaints</h3>
            </div>
            <div className="card-body card-body-flat">
              {rows.length ? (
                rows.slice(0, 8).map((item) => (
                  <div className="complaint-row" key={item.complaint_id} data-testid={`applicant-row-${item.complaint_id}`}>
                    <span className="comp-id">{item.complaint_id}</span>
                    <span className="comp-title">{item.category}</span>
                    <span className={`status-pill ${item.statusClass}`}>{item.status}</span>
                    <span className="comp-date">L{item.current_level}</span>
                  </div>
                ))
              ) : (
                <div className="empty-state">No complaints filed yet.</div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {activeNav === "newcase" ? (
        <div className="card" data-testid="applicant-newcase-card">
          <div className="card-head">
            <h3>Start AI Complaint Intake</h3>
          </div>
          <div className="card-body form-grid">
            <select value={category} onChange={(event) => setCategory(event.target.value)} data-testid="applicant-category-select">
              <option>Land Dispute</option>
              <option>Family Dispute</option>
              <option>Dowry</option>
              <option>Other</option>
            </select>
            <input value={boothId} onChange={(event) => setBoothId(event.target.value)} placeholder="Optional Target Booth ID" data-testid="applicant-target-booth-input" />
            <Button onClick={startComplaint} data-testid="applicant-intake-start-button">Proceed to Intake</Button>
          </div>
        </div>
      ) : null}

      {activeNav === "history" ? (
        <RecentComplaintsCard
          title="Full Complaint History"
          complaints={rows}
          onOpen={() => {
            toast.info("Detail tracking is visible in status, escalation and notifications.");
          }}
        />
      ) : null}
    </DashboardWorkbench>
  );
};
